import React, { useState } from "react";
import { X, Loader2, CheckCircle2 } from "lucide-react";

/**
 * A modal for approving a deposit with optional reserve and OTC platform percentages.
 * 
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Function to close the modal.
 * @param {function} onSubmit - Function called with { reservePercentage, otcPlatformPercentage } on submit.
 * @param {string} title - The title of the modal.
 * @param {boolean} isLoading - Whether the submission is in progress.
 */
const ApproveDepositModal = ({ isOpen, onClose, onSubmit, title = "Approve Deposit", isLoading = false, reservePercentage, setReservePercentage, otcPlatformPercentage, setOtcPlatformPercentage, reserveHoldDays, setReserveHoldDays, data }) => {
  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      reservePercentage: reservePercentage ? Number(reservePercentage) : 0,
      otcPlatformPercentage: otcPlatformPercentage ? Number(otcPlatformPercentage) : 0,
      reserveHoldDays: Number(reserveHoldDays),
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] w-full p-4 overflow-hidden">
      <div className="relative p-6 w-full max-w-md bg-white rounded-2xl shadow-2xl transition-all duration-200 transform animate-in fade-in zoom-in">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 transition-colors hover:text-gray-600"
        >
          <X size={20} />
        </button>

        {/* Modal content */}
        <div className="space-y-6">
          <div className="text-center">
            <div className="flex justify-center items-center mx-auto mb-4 w-12 h-12 bg-emerald-100 rounded-full">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="mb-2 font-sans text-xl font-bold text-gray-900">
              {title}
            </h3>

            <p className="font-sans text-sm text-gray-500">
              Set the percentages below for this deposit. Both fields are optional.
            </p>

            <div className="p-1 mt-2 bg-gray-100 rounded-lg">
              <div className="flex gap-2 justify-center items-center">
                <p className="font-sans text-sm text-gray-500">
                  Name: <span className="font-semibold">{data.user?.firstName} {data.user?.lastName}</span>
                </p>

                <p className="font-sans text-sm text-gray-500">
                  Amount: <span className="font-semibold">{data?.currency?.symbol || data?.wallet?.currency?.symbol}{data?.amount?.toLocaleString()}</span>
                </p>

              </div>

              <p className="font-sans text-sm text-gray-500">
                Gateway Ref: <span className="font-semibold">{data?.paymentGatewayReference || "N/A"}</span>
              </p>
            </div>

          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-4">
              <div className="flex flex-col">
                <label className="block mb-2 font-sans text-sm font-semibold text-gray-700">
                  Reserve Percentage (%)
                </label>
                <input
                  type="number"
                  value={reservePercentage}
                  onChange={(e) => setReservePercentage(e.target.value)}
                  placeholder="e.g. 2.50"
                  className="p-3 w-full font-sans text-base rounded-xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col">
                <label className="block mb-2 font-sans text-sm font-semibold text-gray-700">
                  OTC Platform Percentage (%)
                </label>
                <input
                  type="number"
                  value={otcPlatformPercentage}
                  onChange={(e) => setOtcPlatformPercentage(e.target.value)}
                  placeholder="e.g. 2.50"
                  className="p-3 w-full font-sans text-base rounded-xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="block mb-2 font-sans text-sm font-semibold text-gray-700">
                  Reserve Hold Days
                </label>
                <input
                  type="number"
                  value={reserveHoldDays}
                  onChange={(e) => setReserveHoldDays(e.target.value)}
                  placeholder="e.g. 20"
                  className="p-3 w-full font-sans text-base rounded-xl border border-gray-200 transition-all outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 font-sans font-bold text-gray-700 rounded-xl border border-gray-200 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 py-3 px-4 bg-emerald-600 text-white rounded-xl font-bold font-sans hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[120px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Approving...
                  </>
                ) : (
                  "Confirm Approval"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApproveDepositModal;
